import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  Chip,
  CircularProgress,
  Alert,
  InputAdornment,
  Divider,
  Radio,
  RadioGroup,
  FormControlLabel,
  MenuItem,
} from "@mui/material";
import { Search, Person, CheckCircle, Add, Badge, Cake } from "@mui/icons-material";
import { useQuery } from "@tanstack/react-query";
import { useApi } from "../../../api/ApiProvider";
import { BaseResultDtoKindEnum } from "../../../api-client";
import { useCreateOrUpdatePatient } from "../../../stores/Patients/patient.mutations";
import { levenshtein } from "./useExtractionMatching";

// ============================================================================
// Types
// ============================================================================

interface PatientMatchDialogProps {
  open: boolean;
  onClose: () => void;
  onPatientSelected: (patientId: number) => void;
  extractedName: string | null | undefined;
  extractedDob: string | null | undefined;
  extractedInsuranceNo: string | null | undefined;
  locationId?: string;
}

type CandidateMatchReason = "name" | "insurance-number" | "dob-fuzzy-name";

interface PatientCandidate {
  patientId: number;
  firstName: string;
  lastName: string;
  dob: string | null;
  insuranceNo: string | null;
  locationMatch: boolean;
  dobMatch: boolean;
  insuranceNoMatch: boolean;
  fuzzyNameMatch: boolean;
  matchReason: CandidateMatchReason;
}

/** A name variant suggestion for multi-word names */
interface NameVariant {
  /** Display label with comma: "DeLuca, David Scheeder" */
  label: string;
  /** Search query without comma: "DeLuca David Scheeder" */
  searchQuery: string;
  /** Parsed first name portion */
  firstName: string;
  /** Parsed last name portion */
  lastName: string;
}

// ============================================================================
// Name Reversal Logic
// ============================================================================

/**
 * Build name variants from a full OCR name.
 *
 * The OCR typically gives "FirstName LastName" but the search expects
 * "LastName FirstName". For multi-word names we generate alternative splits
 * so the user can pick the correct one.
 *
 * Examples:
 * - "Gisela Sachmann"          → default "Sachmann Gisela", no extra chips
 * - "David Scheeder DeLuca"    → default "DeLuca David", chips: "Scheeder DeLuca, David"
 * - "David de Luca"            → default "Luca David", chips: "de Luca, David"
 */
function buildNameVariants(fullName: string | null | undefined): {
  defaultSearch: string;
  defaultFirstName: string;
  defaultLastName: string;
  variants: NameVariant[];
} {
  if (!fullName || !fullName.trim()) {
    return { defaultSearch: "", defaultFirstName: "", defaultLastName: "", variants: [] };
  }

  const words = fullName.trim().split(/\s+/);

  if (words.length <= 1) {
    return { defaultSearch: words[0], defaultFirstName: "", defaultLastName: words[0], variants: [] };
  }

  // Default: last word is lastName, rest is firstName
  const defaultLastName = words[words.length - 1];
  const defaultFirstName = words.slice(0, -1).join(" ");
  const defaultSearch = `${defaultLastName} ${defaultFirstName}`;

  if (words.length === 2) {
    // Simple two-word name — show one variant chip for the reversed form
    return {
      defaultSearch,
      defaultFirstName,
      defaultLastName,
      variants: [
        {
          label: `${defaultLastName}, ${defaultFirstName}`,
          searchQuery: defaultSearch,
          firstName: defaultFirstName,
          lastName: defaultLastName,
        },
      ],
    };
  }

  // Multi-word name: generate variants for each possible split point
  // Split at position i means words[0..i-1] = firstName, words[i..] = lastName
  const variants: NameVariant[] = [];

  for (let i = 1; i < words.length; i++) {
    const firstName = words.slice(0, i).join(" ");
    const lastName = words.slice(i).join(" ");
    const label = `${lastName}, ${firstName}`;
    const searchQuery = `${lastName} ${firstName}`;

    // Skip the default variant (last word only as lastName)
    if (i === words.length - 1) continue;

    variants.push({ label, searchQuery, firstName, lastName });
  }

  return { defaultSearch, defaultFirstName, defaultLastName, variants };
}

// ============================================================================
// Helpers
// ============================================================================

function normalizeDateForCompare(dateStr: string | null | undefined): string | null {
  if (!dateStr) return null;
  if (/^\d{4}-\d{2}-\d{2}/.test(dateStr)) return dateStr.slice(0, 10);
  const match = dateStr.match(/^(\d{2})\.(\d{2})\.(\d{4})/);
  if (match) return `${match[3]}-${match[2]}-${match[1]}`;
  return null;
}

function formatDob(dateStr: string | null): string {
  if (!dateStr) return "–";
  const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) return `${match[3]}.${match[2]}.${match[1]}`;
  return dateStr;
}

function dobToIso(dateStr: string | null | undefined): string | undefined {
  if (!dateStr) return undefined;
  return normalizeDateForCompare(dateStr) ?? undefined;
}

// ============================================================================
// Component
// ============================================================================

const FUZZY_NAME_THRESHOLD = 0.35;

const PatientMatchDialog: React.FC<PatientMatchDialogProps> = ({
  open,
  onClose,
  onPatientSelected,
  extractedName,
  extractedDob,
  extractedInsuranceNo,
  locationId,
}) => {
  const { patients } = useApi();
  const createPatientMutation = useCreateOrUpdatePatient();

  const nameInfo = useMemo(() => buildNameVariants(extractedName), [extractedName]);

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedMode, setSelectedMode] = useState<"new" | number>("new");
  const [creating, setCreating] = useState(false);
  const [createSalutation, setCreateSalutation] = useState("");
  const [createFirstName, setCreateFirstName] = useState("");
  const [createLastName, setCreateLastName] = useState("");

  const normalizedInsuranceNo = extractedInsuranceNo?.trim() || "";
  const normalizedDobForSearch = normalizeDateForCompare(extractedDob) || "";

  const normalizedExtractedDob = useMemo(
    () => normalizeDateForCompare(extractedDob),
    [extractedDob]
  );

  // Reset when dialog opens
  useEffect(() => {
    if (open) {
      setSearchQuery(nameInfo.defaultSearch);
      setCreateSalutation("");
      setCreateFirstName(nameInfo.defaultFirstName);
      setCreateLastName(nameInfo.defaultLastName);
      setCreating(false);
      setSelectedMode("new");
    }
  }, [open, nameInfo]);

  // Search patients by name (user-editable search field)
  const { data: searchResults, isLoading } = useQuery({
    queryKey: ["patient-match-search", searchQuery],
    queryFn: async () => {
      try {
        return await patients.search(searchQuery, locationId, undefined, undefined, 50);
      } catch {
        return null;
      }
    },
    enabled: open && searchQuery.trim().length >= 2,
    staleTime: 30_000,
    retry: false,
  });

  // Search by insurance number (automatic, runs alongside name search)
  const { data: insuranceResults, isLoading: insuranceLoading } = useQuery({
    queryKey: ["patient-match-insurance", normalizedInsuranceNo],
    queryFn: async () => {
      try {
        return await patients.search(undefined, undefined, normalizedInsuranceNo, undefined, 5);
      } catch {
        return null;
      }
    },
    enabled: open && normalizedInsuranceNo.length >= 3,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  // Auto-select when Versichertennummer resolves to exactly one patient
  useEffect(() => {
    if (!open || normalizedInsuranceNo.length < 3) return;
    const insurancePatients =
      insuranceResults?.results?.filter(
        (r) => r.kind === BaseResultDtoKindEnum.Patient && r.patientId != null
      ) ?? [];
    if (insurancePatients.length === 1) {
      setSelectedMode(insurancePatients[0].patientId!);
    }
  }, [open, normalizedInsuranceNo, insuranceResults]);

  // Search by DOB (automatic, to find misspelt names)
  const { data: dobResults, isLoading: dobSearchLoading } = useQuery({
    queryKey: ["patient-match-dob", normalizedDobForSearch],
    queryFn: async () => {
      try {
        return await patients.search(undefined, undefined, undefined, normalizedDobForSearch, 30);
      } catch {
        return null;
      }
    },
    enabled: open && normalizedDobForSearch.length === 10,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const sortedCandidates = useMemo<PatientCandidate[]>(() => {
    const seen = new Set<number>();
    const candidates: PatientCandidate[] = [];

    const extractedLastName = extractedName?.trim().split(/\s+/).pop()?.toLowerCase() || "";

    // 1. Insurance number matches (highest priority)
    const insurancePatients = insuranceResults?.results?.filter(
      (r) => r.kind === BaseResultDtoKindEnum.Patient && r.patientId != null
    ) ?? [];
    for (const r of insurancePatients) {
      if (seen.has(r.patientId!)) continue;
      seen.add(r.patientId!);
      const candidateDob = r.dob ? r.dob.slice(0, 10) : null;
      candidates.push({
        patientId: r.patientId!,
        firstName: r.firstName ?? "",
        lastName: r.lastName ?? "",
        dob: candidateDob,
        insuranceNo: r.insuranceNo ?? null,
        locationMatch: r.locationMatch,
        dobMatch: !!normalizedExtractedDob && candidateDob === normalizedExtractedDob,
        insuranceNoMatch: true,
        fuzzyNameMatch: false,
        matchReason: "insurance-number",
      });
    }

    // 2. Name search results
    const namePatients = searchResults?.results?.filter(
      (r) => r.kind === BaseResultDtoKindEnum.Patient && r.patientId != null
    ) ?? [];
    for (const r of namePatients) {
      if (seen.has(r.patientId!)) continue;
      seen.add(r.patientId!);
      const candidateDob = r.dob ? r.dob.slice(0, 10) : null;
      candidates.push({
        patientId: r.patientId!,
        firstName: r.firstName ?? "",
        lastName: r.lastName ?? "",
        dob: candidateDob,
        insuranceNo: r.insuranceNo ?? null,
        locationMatch: r.locationMatch,
        dobMatch: !!normalizedExtractedDob && candidateDob === normalizedExtractedDob,
        insuranceNoMatch: false,
        fuzzyNameMatch: false,
        matchReason: "name",
      });
    }

    // 3. DOB matches with fuzzy last name (misspelling detection)
    if (extractedLastName) {
      const dobPatients = dobResults?.results?.filter(
        (r) => r.kind === BaseResultDtoKindEnum.Patient && r.patientId != null
      ) ?? [];
      for (const r of dobPatients) {
        if (seen.has(r.patientId!)) continue;
        if (!r.lastName) continue;
        const distance = levenshtein(r.lastName.toLowerCase(), extractedLastName);
        const maxLen = Math.max(r.lastName.length, extractedLastName.length);
        if (maxLen > 0 && distance / maxLen <= FUZZY_NAME_THRESHOLD) {
          seen.add(r.patientId!);
          const candidateDob = r.dob ? r.dob.slice(0, 10) : null;
          candidates.push({
            patientId: r.patientId!,
            firstName: r.firstName ?? "",
            lastName: r.lastName ?? "",
            dob: candidateDob,
            insuranceNo: r.insuranceNo ?? null,
            locationMatch: r.locationMatch,
            dobMatch: true,
            insuranceNoMatch: false,
            fuzzyNameMatch: true,
            matchReason: "dob-fuzzy-name",
          });
        }
      }
    }

    // Sort: insurance match > DOB match > location match > alphabetical
    const reasonPriority: Record<CandidateMatchReason, number> = {
      "insurance-number": 0,
      "dob-fuzzy-name": 1,
      "name": 2,
    };
    return candidates.sort((a, b) => {
      const rp = reasonPriority[a.matchReason] - reasonPriority[b.matchReason];
      if (rp !== 0) return rp;
      if (a.dobMatch !== b.dobMatch) return a.dobMatch ? -1 : 1;
      if (a.locationMatch !== b.locationMatch) return a.locationMatch ? -1 : 1;
      return `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`);
    });
  }, [searchResults, insuranceResults, dobResults, normalizedExtractedDob, extractedName]);

  const handleVariantClick = (variant: NameVariant) => {
    setSearchQuery(variant.searchQuery);
    setCreateFirstName(variant.firstName);
    setCreateLastName(variant.lastName);
  };

  const handleConfirm = async () => {
    if (selectedMode === "new") {
      setCreating(true);
      try {
        const result = await createPatientMutation.mutateAsync({
          salutation: createSalutation || undefined,
          firstName: createFirstName,
          lastName: createLastName,
          dob: dobToIso(extractedDob),
          addresses: [],
        } as any);
        const newPatientId = result.patient?.id;
        if (newPatientId) {
          onPatientSelected(newPatientId);
          onClose();
        }
      } finally {
        setCreating(false);
      }
    } else {
      onPatientSelected(selectedMode);
      onClose();
    }
  };

  const isNewPatient = selectedMode === "new";
  const canConfirm = isNewPatient ? !!createLastName.trim() : true;
  const saving = creating || createPatientMutation.isPending;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Patient zuordnen</DialogTitle>

      <DialogContent dividers>
        {/* Extracted info summary */}
        {(extractedName || extractedDob || extractedInsuranceNo) && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Extrahiert:</strong>{" "}
              {extractedName || "–"}
              {extractedDob && ` · geb. ${extractedDob}`}
              {extractedInsuranceNo && ` · Vers.-Nr. ${extractedInsuranceNo}`}
            </Typography>
          </Alert>
        )}

        <RadioGroup
          value={String(selectedMode)}
          onChange={(e) => {
            const val = e.target.value;
            setSelectedMode(val === "new" ? "new" : Number(val));
          }}
        >
          {/* ── Create new patient option ──────────────────── */}
          <FormControlLabel
            value="new"
            control={<Radio size="small" />}
            label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Add fontSize="small" />
                <Typography variant="body2">Neuen Patienten anlegen</Typography>
              </Box>
            }
            sx={{ mb: 0.5 }}
          />

          {/* Editable fields when "new" is selected */}
          {isNewPatient && (
            <Box sx={{ ml: 3.5, mb: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
              {/* Name variant chips */}
              {nameInfo.variants.length > 0 && (
                <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                  {nameInfo.variants.map((v) => (
                    <Chip
                      key={v.label}
                      label={v.label}
                      size="small"
                      variant={createLastName === v.lastName && createFirstName === v.firstName ? "filled" : "outlined"}
                      color={createLastName === v.lastName && createFirstName === v.firstName ? "primary" : "default"}
                      onClick={() => handleVariantClick(v)}
                      sx={{ cursor: "pointer" }}
                    />
                  ))}
                </Box>
              )}
              <TextField
                size="small"
                label="Anrede"
                value={createSalutation}
                onChange={(e) => setCreateSalutation(e.target.value)}
                select
                sx={{ maxWidth: 180 }}
              >
                <MenuItem value="">–</MenuItem>
                <MenuItem value="Herr">Herr</MenuItem>
                <MenuItem value="Frau">Frau</MenuItem>
                <MenuItem value="Divers">Divers</MenuItem>
              </TextField>
              <TextField
                size="small"
                label="Nachname"
                value={createLastName}
                onChange={(e) => setCreateLastName(e.target.value)}
                required
              />
              <TextField
                size="small"
                label="Vorname"
                value={createFirstName}
                onChange={(e) => setCreateFirstName(e.target.value)}
              />
              {extractedDob && (
                <Typography variant="caption" color="text.secondary">
                  geb. {extractedDob}
                </Typography>
              )}
            </Box>
          )}

          <Divider sx={{ my: 1 }} />

          {/* ── Search for existing patient ────────────────── */}
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Oder vorhandenen Patienten auswählen
          </Typography>

          <TextField
            fullWidth
            size="small"
            label="Patient suchen"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 1.5 }}
          />

          {/* Search results */}
          {isLoading || insuranceLoading || dobSearchLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : sortedCandidates.length === 0 && searchQuery.trim().length < 2 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 1 }}>
              Mindestens 2 Zeichen eingeben
            </Typography>
          ) : sortedCandidates.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 1 }}>
              Keine vorhandenen Patienten gefunden
            </Typography>
          ) : (
            <Box sx={{ maxHeight: 300, overflow: "auto" }}>
              {sortedCandidates.map((c) => (
                <FormControlLabel
                  key={c.patientId}
                  value={String(c.patientId)}
                  control={<Radio size="small" />}
                  label={
                    <Box sx={{ ml: 0.5 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
                        <Person fontSize="small" color="action" />
                        <Typography variant="body2" fontWeight={500}>
                          {c.lastName}, {c.firstName}
                        </Typography>
                        {c.insuranceNoMatch && (
                          <Chip
                            icon={<Badge sx={{ fontSize: 14 }} />}
                            label="Vers.-Nr. stimmt"
                            size="small"
                            color="success"
                            sx={{ height: 20, fontSize: "0.65rem" }}
                          />
                        )}
                        {c.dobMatch && (
                          <Chip
                            icon={<CheckCircle sx={{ fontSize: 14 }} />}
                            label="Geb. stimmt"
                            size="small"
                            color="success"
                            variant="outlined"
                            sx={{ height: 20, fontSize: "0.65rem" }}
                          />
                        )}
                        {c.fuzzyNameMatch && (
                          <Chip
                            icon={<Cake sx={{ fontSize: 14 }} />}
                            label="Name ähnlich"
                            size="small"
                            color="warning"
                            variant="outlined"
                            sx={{ height: 20, fontSize: "0.65rem" }}
                          />
                        )}
                        {c.locationMatch && (
                          <Chip
                            label="Standort"
                            size="small"
                            color="info"
                            variant="outlined"
                            sx={{ height: 20, fontSize: "0.65rem" }}
                          />
                        )}
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 3.5 }} component="div">
                        geb. {formatDob(c.dob)} · ID {c.patientId}
                        {c.insuranceNo && ` · Vers.-Nr. ${c.insuranceNo}`}
                      </Typography>
                    </Box>
                  }
                  sx={{ alignItems: "flex-start", mb: 0.5 }}
                />
              ))}
            </Box>
          )}
        </RadioGroup>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Abbrechen
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={!canConfirm || saving}
          startIcon={saving ? <CircularProgress size={16} /> : undefined}
          color={isNewPatient ? "success" : "primary"}
        >
          {saving
            ? "Wird gespeichert…"
            : isNewPatient
            ? "Patient anlegen & zuordnen"
            : "Patient zuordnen"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PatientMatchDialog;
